import { useState, useEffect } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';
import { mockPaymentMethods } from '../../../mocks/supplierInvoices';

interface PaymentFormProps {
  invoice: any;
  onClose: () => void;
  onSave: (updatedInvoice: any) => void;
}

export default function PaymentForm({ invoice, onClose, onSave }: PaymentFormProps) {
  const [formData, setFormData] = useState({
    montant: '',
    date: new Date().toISOString().split('T')[0],
    modePaiement: 'VIREMENT',
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  /* =========================
   * Pré-remplissage avec le restant à payer
   * ========================= */
  useEffect(() => {
    if (!invoice || invoice.montant_restant == null) return;

    setFormData(prev => ({
      ...prev,
      montant: String(invoice.montant_restant),
    }));
  }, [invoice]);

  /* =========================
   * Helpers
   * ========================= */
  const formatAmount = (amount: number = 0, devise: string = 'GNF') => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' ' + devise;
  };

  const getModePaymentIcon = (mode: string) => {
    switch (mode) {
      case 'VIREMENT': return 'ri-bank-line';
      case 'CHEQUE': return 'ri-file-paper-line';
      case 'ESPECES': return 'ri-money-euro-circle-line';
      default: return 'ri-money-euro-circle-line';
    }
  };

  /* =========================
   * Gestion des changements
   * ========================= */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setFormData(prev => ({ ...prev, [name]: value }));

    // Effacer erreur
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Génération auto de la référence
    if (name === 'modePaiement') {
      const today = new Date();
      const prefix = value === 'VIREMENT' ? 'VIR' :
                     value === 'CHEQUE' ? 'CHQ' :
                     value === 'ESPECES' ? 'ESP' : 'PAY';

      const ref = `${prefix}-${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;

      setFormData(prev => ({ ...prev, reference: ref }));
    }
  };

  /* =========================
   * Validation
   * ========================= */
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    const montant = parseFloat(formData.montant);

    if (isNaN(montant) || montant <= 0) {
      newErrors.montant = 'Le montant doit être supérieur à 0';
    }

    if (montant > invoice.montant_restant) {
      newErrors.montant = 'Le montant ne peut pas dépasser le restant à payer';
    }

    if (!formData.date) {
      newErrors.date = 'La date est obligatoire';
    }

    if (!formData.modePaiement) {
      newErrors.modePaiement = 'Veuillez choisir un mode de paiement';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* =========================
   * Soumission réelle vers l'API
   * ========================= */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    const payload = {
      montant: parseFloat(formData.montant),
      date_paiement: formData.date,
      mode: formData.modePaiement,
      notes: formData.notes,
    };

    try {
      const response = await createResource(
        `factures-fournisseur/${invoice.id}/paiement`,
        payload      );

      toast.success('Règlement enregistré avec succès !');
      onSave(response.data.data); // La facture mise à jour est renvoyée par l'API
      onClose();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Erreur lors de l’enregistrement du règlement';
      toast.error(message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const montantSaisi = parseFloat(formData.montant) || 0;
  const pourcentagePaiement = invoice.total_ttc > 0 ? (montantSaisi / invoice.total_ttc) * 100 : 0;

  /* =========================
   * RENDER - Design demandé
   * ========================= */
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center space-x-4">
          <button
            onClick={onClose}
            className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer"
            title="Retour"
          >
            <i className="ri-arrow-left-line text-xl text-gray-600"></i>
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Nouveau Règlement</h2>
            <p className="text-sm text-gray-600 mt-1">
              Facture {invoice.numero_facture_fournisseur} — {invoice.fournisseur?.raison_sociale}
            </p>
          </div>
        </div>
      </div>

      {/* Infos facture */}
      <div className="p-6 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Montant Total TTC</label>
            <p className="text-lg font-bold text-gray-900">{formatAmount(invoice.total_ttc)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Déjà Réglé</label>
            <p className="text-lg font-semibold text-green-600">{formatAmount(invoice.montant_paye)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Restant à Payer</label>
            <p className="text-lg font-bold text-red-600">{formatAmount(invoice.montant_restant)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Date d'Échéance</label>
            <p className="text-lg text-gray-900">
              {new Date(invoice.date_echeance).toLocaleDateString('fr-FR')}
            </p>
            {invoice.jours_avant_echeance < 0 && (
              <p className="text-sm text-red-600 font-medium mt-1">
                En retard de {Math.abs(invoice.jours_avant_echeance)} jour(s)
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Formulaire */}
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Montant + Mode */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Montant du Règlement *
            </label>
            <div className="relative">
              <input
                type="text" // ← Changé en text pour accepter la virgule
                name="montant"
                value={formData.montant}
                onChange={(e) => {
                  // Autorise seulement chiffres, virgule et point
                  let value = e.target.value.replace(/[^0-9.,]/g, '');

                  // Remplace toutes les virgules par des points pour uniformité interne
                  value = value.replace(',', '.');

                  // Empêche plusieurs points
                  const parts = value.split('.');
                  if (parts.length > 2) {
                    value = parts[0] + '.' + parts.slice(1).join('');
                  }

                  // Optionnel : limite à 2 décimales
                  if (value.includes('.')) {
                    const [int, dec] = value.split('.');
                    if (dec && dec.length > 2) {
                      value = int + '.' + dec.slice(0, 2);
                    }
                  }

                  setFormData(prev => ({ ...prev, montant: value }));

                  if (errors.montant) {
                    setErrors(prev => ({ ...prev, montant: '' }));
                  }
                }}
                required
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                  errors.montant ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Ex: 63,75"
              />
              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                GNF
              </span>
            </div>
            {errors.montant && <p className="mt-1 text-sm text-red-600">{errors.montant}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mode de Paiement *
            </label>
            <div className="relative">
              <select
                name="modePaiement"
                value={formData.modePaiement}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none"
              >
                {mockPaymentMethods.map(method => (
                  <option key={method.id} value={method.id}>
                    {method.label}
                  </option>
                ))}
              </select>
              <i className={`${getModePaymentIcon(formData.modePaiement)} absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl`}></i>
            </div>
          </div>
        </div>

        {/* Date + Référence */}
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de Paiement *
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              required
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                errors.date ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.date && <p className="mt-1 text-sm text-red-600">{errors.date}</p>}
          </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes et Observations
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Référence bancaire, chèque n°, commentaires..."
          />
        </div>

        {/* Résumé */}
        {montantSaisi > 0 && (
          <div className="bg-green-50 p-5 rounded-lg border border-green-200">
            <h4 className="text-base font-semibold text-green-900 mb-3">Résumé du règlement</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-green-700">Montant saisi :</span>
                <span className="font-semibold text-green-900 ml-2">{formatAmount(montantSaisi)}</span>
              </div>
              <div>
                <span className="text-green-700">Nouveau total réglé :</span>
                <span className="font-semibold text-green-900 ml-2">
                  {formatAmount(invoice.montant_paye + montantSaisi)}
                </span>
              </div>
              <div>
                <span className="text-green-700">Nouveau solde :</span>
                <span className="font-semibold text-green-900 ml-2">
                  {formatAmount(invoice.montant_restant - montantSaisi)}
                </span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-green-200">
              <span className="text-green-700 font-medium">Statut après paiement :</span>
              <span className="font-bold text-green-900 ml-2">
                {invoice.montant_restant - montantSaisi <= 0 ? 'Soldée' : 'Payée partiellement'}
              </span>
            </div>
          </div>
        )}

        {/* Boutons */}
        <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
          >
            {loading && <i className="ri-loader-4-line animate-spin"></i>}
            <i className="ri-money-euro-circle-line"></i>
            <span>Enregistrer le règlement</span>
          </button>
        </div>
      </form>
    </div>
  );
}